import { useRef, useState } from "react";
import { ImagePlus, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { MaskLines } from "@/components/landing/Reveal";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, formatApiError } from "@/lib/api";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", company: user?.company || "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);

  const save = async (patch) => {
    const { data } = await api.put("/profile", patch);
    setUser(data);
    return data;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await save(form); toast.success("Perfil actualizado"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const onLogo = async (file) => {
    if (!file?.type?.startsWith("image/")) return toast.error("Selecciona una imagen PNG o JPG");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads?kind=logo", fd);
      await save({ logo_path: data.path });
      toast.success("Logotipo actualizado");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const removeLogo = async () => {
    try { await save({ logo_path: "" }); toast.success("Logotipo eliminado"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <main className="min-h-screen" data-testid="profile-page">
      <Navbar variant="app" />
      <div className="mx-auto max-w-[1100px] px-5 sm:px-8 pt-32 pb-24">
        <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">Perfil de empresa</div>
        <h1 className="font-display-x mt-5 text-5xl sm:text-7xl font-black uppercase leading-[0.9] tracking-tight"><MaskLines lines={["Tu marca", "en el reporte"]} /></h1>

        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <section className="lg:col-span-5 min-w-0">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/65">Logotipo de la empresa</div>
            <div className="mt-3 aspect-[3/2] border border-dashed border-white/20 bg-[#111722] grid place-items-center p-6 relative" data-testid="profile-logo-box">
              {user?.logo_path ? (
                <div className="w-full h-full bg-white grid place-items-center p-4">
                  <img src={fileUrl(user.logo_path)} alt="Logotipo de la empresa" className="max-w-full max-h-full object-contain" data-testid="profile-logo-preview" />
                </div>
              ) : (
                <div className="text-center text-[#F5F1E8]/60 text-sm"><ImagePlus className="w-8 h-8 mx-auto mb-3 text-[#F59E0B]" strokeWidth={1.4} />Sin logotipo</div>
              )}
              {uploading && <div className="absolute inset-0 bg-[#0B0F17]/70 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#F59E0B]" /></div>}
            </div>
            <p className="mt-3 text-sm text-[#F5F1E8]/65">Aparecerá en la esquina superior izquierda de tus reportes PDF. Recomendado: PNG con fondo transparente.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={() => ref.current?.click()} disabled={uploading} data-testid="profile-logo-upload-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 hover:border-[#F59E0B] hover:text-[#F59E0B] transition-colors text-sm font-semibold">
                <ImagePlus className="w-4 h-4" /> {user?.logo_path ? "Cambiar logotipo" : "Subir logotipo"}
              </button>
              {user?.logo_path && (
                <button type="button" onClick={removeLogo} data-testid="profile-logo-remove-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-sm hover:border-rose-400 hover:text-rose-400 transition-colors">
                  <Trash2 className="w-4 h-4" /> Quitar
                </button>
              )}
            </div>
            <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="profile-logo-input" onChange={(e) => { onLogo(e.target.files?.[0]); e.target.value = ""; }} />
          </section>

          <form onSubmit={onSubmit} className="lg:col-span-7 space-y-8" data-testid="profile-form">
            {[["name", "Nombre del responsable", "profile-name-input"], ["company", "Empresa / Razón social", "profile-company-input"]].map(([k, label, tid]) => (
              <label key={k} className="block">
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/65">{label}</span>
                <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={tid} className="mt-2 w-full bg-transparent border-b border-white/20 py-3 text-xl outline-none focus:border-[#F59E0B] transition-colors" />
              </label>
            ))}
            <div className="text-sm text-[#F5F1E8]/60">Correo: <span className="text-[#F5F1E8]/85">{user?.email}</span></div>
            <button disabled={busy} data-testid="profile-save-button" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#F59E0B] text-[#0B0F17] font-semibold hover:bg-[#F5F1E8] transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar perfil
            </button>
          </form>
        </div>
      </div>
    </main>
  );
}
